import axios from "../axios";
import { parseContentDispositionFilename } from "../../utils/http";

const api = {
  createQuestionnaire(data, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.post("/organization-questionnaires", data, headers).then(({ data }) => data);
  },

  bulkCreateQuestionnaires(data, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.post("/organization-questionnaires/bulk", data, headers).then(({ data }) => data);
  },

  updateQuestionnaire({ id, data }, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.put(`/organization-questionnaires/${id}`, data, headers).then(({ data }) => data);
  },

  getQuestionnaireById(id, accessToken) {
    const headers = { headers: { "Authorization": `Bearer ${accessToken}` } };
    return axios.get(`/organization-questionnaires/${id}`, headers).then(({ data }) => data);
  },

  getAllQuestionnaires(queryParams, accessToken) {
    const config = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.get(`/organization-questionnaires?${queryParams}`, config).then(({ data }) => data);
  },

  deleteQuestionnaire(id, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.delete(`/organization-questionnaires/${id}`, headers).then(({ data }) => data);
  },

  finalizeQuestionnaire(id, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.post(`/organization-questionnaires/${id}/finalize`, id, headers).then(({ data }) => data);
  },

  importScopeOneAndTwoData({ questionnaireId, data, groupType }, accessToken) {
    const config = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    if (groupType) {
      config.params = { groupType };
    }
    return axios.post(`/organization-questionnaires/${questionnaireId}/import`, data, config).then(({ data }) => data);
  },

  getOrganisationQuestionnaires(params, accessToken) {
    const config = {
      params: params,
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.get("/organization-questionnaires-data", config).then(({ data }) => data);
  },

  // Insights "Source Data" cards: FINALIZED data-collection tasks that have entered data for the
  // given scope + period + dimension filter. `scope` is a ScopeCategoryKey (e.g. SCOPE_1, SCOPE_2,
  // CAPITAL_GOODS). `dimensionItemIds` is the same pre-serialized fragment the insights endpoints
  // use (e.g. "dimensionOne=1,2&dimensionTwo=3"); the backend binds those to List<Long> per level.
  // quarter null/"ALL" is dropped so the backend applies its Quarter.ALL (whole-year) default.
  getSourceDataTasks({ scope, year, quarter, dimensionItemIds }, accessToken) {
    const config = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    const dimensionParam = dimensionItemIds ? `&${dimensionItemIds}` : "";
    const quarterParam = quarter && quarter !== "ALL" ? `&quarter=${quarter}` : "";
    return axios
      .get(`/organization-questionnaires/source-data-tasks?scope=${scope}&year=${year}${quarterParam}${dimensionParam}`, config)
      .then(({ data }) => data);
  },

  getQuestionnaireDataById(id, accessToken) {
    const headers = { headers: { "Authorization": `Bearer ${accessToken}` } };
    return axios.get(`/organization-questionnaires-data/${id}`, headers).then(({ data }) => data);
  },

  unfinaliseOrgQuestionnaire(questionnaireId, accessToken) {
    const headers = { headers: { "Authorization": `Bearer ${accessToken}` } };
    return axios.patch(`/organization-questionnaires/${questionnaireId}/unfinalize`, {}, headers).then(({ data }) => data);
  },

  updateMethodology({ questionnaireId, data }, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.patch(`/organization-questionnaires/${questionnaireId}/methodology`, data, headers).then(({ data }) => data);
  },

  updateQuestionnaireFiles({ questionnaireId, data }, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.patch(`/organization-questionnaires/${questionnaireId}/files`, data, headers).then(({ data }) => data);
  },

  // ESG-3927: ESG_ADMIN-only override of the Scope 2 engine (V1/V2) for a
  // single questionnaire. scopeTwoVersion null/absent clears the override.
  updateScopeTwoVersion({ questionnaireId, scopeTwoVersion }, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.patch(`/organization-questionnaires/${questionnaireId}/scope-two-version`,
      { scopeTwoVersion }, headers).then(({ data }) => data);
  },

  downloadScopeOneTemplate({ groupType } = {}, accessToken) {
    const config = {
      responseType: "arraybuffer",
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    if (groupType) {
      config.params = { groupType };
    }
    return axios.get(
      `/organization-questionnaires/scope-one/template`, config
    ).then(({ data, headers }) => {
      const xlsxType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([data], { type: xlsxType }));
      const link = document.createElement("a");
      const defaultFileName = parseContentDispositionFilename(
        headers["content-disposition"], "Scope_One_template.xlsx");
      link.href = url;
      link.setAttribute("download", defaultFileName);
      document.body.appendChild(link);
      link.click();
      return true;
    });
  },

  // Manual "Send reminder" action from the task card's kebab menu: { ccUserIds, subject, message }.
  // The server always sends To the task's own assignee; it answers the stored reminder
  // ({ sentAt, sentByName, sentByEmail, recipientEmail, ccCount }).
  sendTaskReminder({ id, data }, accessToken) {
    const headers = {
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.post(`/organization-questionnaires/${id}/reminders`, data, headers).then(({ data }) => data);
  },

  downloadScopeTwoTemplate(accessToken) {
    const config = {
      responseType: "arraybuffer",
      headers: { "Authorization": `Bearer ${accessToken}` }
    };
    return axios.get(
      `/organization-questionnaires/scope-two/template`, config
    ).then(({ data, headers }) => {
      const xlsxType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([data], { type: xlsxType }));
      const link = document.createElement("a");
      const defaultFileName = parseContentDispositionFilename(
        headers["content-disposition"], "Scope_Two_template.xlsx");
      link.href = url;
      link.setAttribute("download", defaultFileName);
      document.body.appendChild(link);
      link.click();
      return true;
    });
  }

};

export default api;
